import { Component, effect, inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import { SelectOption } from '../RadioButton/radiobutton-edit';
import {
  MultiRadioButtonCustomization,
  MultiRadioGroup,
} from './multi-radiobutton-edit';

type OptionFormGroup = FormGroup<{
  label: FormControl<string | null>;
  value: FormControl<string | number | null>;
  valueEqualsDescription: FormControl<boolean | null>;
  type: FormControl<'string' | 'number' | null>;
  weight: FormControl<number | null>;
}>;

type GroupFormGroup = FormGroup<{
  key: FormControl<string | null>;
  label: FormControl<string | null>;
  defaultValue: FormControl<string | number | null>;
  options: FormArray<OptionFormGroup>;
}>;

@Component({
  selector: 'lib-multi-radiobutton-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [form]="customizationForm"
      header="Personalizzazione Multi Radio Button"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div
          [formGroup]="customizationForm"
          class="mrb-panel flex flex-col gap-4 text-sm"
        >
          <div class="flex flex-col gap-3 sm:flex-row">
            <div class="flex grow flex-col gap-1">
              <label for="desc" class="text-slate-600">Descrizione</label>
              <textarea
                id="desc"
                pTextarea
                rows="2"
                formControlName="description"
              ></textarea>
            </div>
            <div class="flex shrink-0 flex-col gap-1 sm:w-44">
              <label for="cap" class="text-slate-600">
                Punteggio massimo (cap)
              </label>
              <p-input-number
                [autofocus]="false"
                inputId="cap"
                formControlName="cap"
                mode="decimal"
                minFractionDigits="0"
                maxFractionDigits="2"
                size="small"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="font-semibold text-slate-700">Gruppi</span>
              <p-button
                icon="pi pi-plus"
                [text]="true"
                [rounded]="true"
                size="small"
                severity="secondary"
                ariaLabel="Aggiungi gruppo"
                (onClick)="addGroup()"
              />
            </div>

            @for (
              groupCtrl of customizationForm.controls.groups.controls;
              track $index;
              let g = $index
            ) {
              <div
                [formGroup]="groupCtrl"
                class="overflow-hidden rounded-lg border border-slate-200 shadow-sm"
              >
                <div
                  class="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5"
                >
                  <input
                    [id]="'group-label-' + g"
                    pInputText
                    pSize="small"
                    placeholder="Titolo gruppo"
                    formControlName="label"
                    class="group-title grow"
                  />
                  <p-button
                    icon="pi pi-trash"
                    [text]="true"
                    [rounded]="true"
                    size="small"
                    severity="danger"
                    ariaLabel="Rimuovi gruppo"
                    (onClick)="removeGroup(g)"
                  />
                </div>

                <div class="p-3">
                  <p-table
                    [value]="groupCtrl.controls.options.controls"
                    class="p-datatable-sm"
                    tableStyleClass="p-fluid"
                  >
                    <ng-template pTemplate="header">
                      <tr>
                        <th>Descrizione</th>
                        <th class="w-20 text-center">= Descr.</th>
                        <th>Valore</th>
                        <th class="w-24">Peso</th>
                        <th class="w-28">Tipo</th>
                        <th class="w-10 text-center">
                          <p-button
                            icon="pi pi-plus"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            severity="secondary"
                            ariaLabel="Aggiungi opzione"
                            (onClick)="addOption(g)"
                          />
                        </th>
                      </tr>
                    </ng-template>
                    <ng-template
                      pTemplate="body"
                      let-optionCtrl
                      let-rowIndex="rowIndex"
                    >
                      <tr [formGroup]="optionCtrl">
                        <td>
                          <input
                            pInputText
                            pSize="small"
                            placeholder="Descrizione"
                            formControlName="label"
                          />
                        </td>
                        <td class="text-center">
                          <p-checkbox
                            [binary]="true"
                            size="small"
                            formControlName="valueEqualsDescription"
                          ></p-checkbox>
                        </td>
                        <td>
                          <input
                            pInputText
                            pSize="small"
                            placeholder="Valore"
                            formControlName="value"
                          />
                        </td>
                        <td>
                          <p-input-number
                            [autofocus]="false"
                            placeholder="Peso"
                            formControlName="weight"
                            mode="decimal"
                            minFractionDigits="0"
                            maxFractionDigits="2"
                            size="small"
                          />
                        </td>
                        <td>
                          <p-select
                            [autofocus]="false"
                            [options]="optionTypes"
                            optionLabel="label"
                            optionValue="value"
                            appendTo="body"
                            size="small"
                            formControlName="type"
                          />
                        </td>
                        <td class="text-center">
                          <p-button
                            icon="pi pi-times"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            severity="secondary"
                            ariaLabel="Rimuovi opzione"
                            (onClick)="removeOption(g, rowIndex)"
                          />
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                </div>

                <div
                  class="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-slate-200 bg-slate-50/60 px-3 py-2"
                  role="group"
                  [attr.aria-labelledby]="'default-' + g"
                >
                  <span [id]="'default-' + g" class="mr-1 text-slate-600">
                    Valore predefinito
                  </span>
                  @for (
                    option of groupCtrl.controls.options.value;
                    track $index;
                    let i = $index
                  ) {
                    <div class="flex items-center gap-2">
                      <p-radioButton
                        [autofocus]="false"
                        [value]="option.value"
                        [inputId]="'opt_' + g + '_' + i"
                        formControlName="defaultValue"
                      ></p-radioButton>
                      <label [for]="'opt_' + g + '_' + i">
                        {{ option?.label }}
                      </label>
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <div class="flex items-center justify-between">
            <label for="required" class="text-slate-600">
              Campo obbligatorio
            </label>
            <p-checkbox
              id="required"
              formControlName="required"
              [binary]="true"
            ></p-checkbox>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    SelectModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    RadioButtonModule,
    CheckboxModule,
    TableModule,
    TextareaModule,
  ],
  styles: [
    `
      /* Tighter, vertically-centered table cells */
      .mrb-panel ::ng-deep .p-datatable-sm .p-datatable-thead > tr > th,
      .mrb-panel ::ng-deep .p-datatable-sm .p-datatable-tbody > tr > td {
        padding: 0.3rem 0.4rem;
        vertical-align: middle;
      }
      .mrb-panel ::ng-deep .p-datatable-sm .p-datatable-thead > tr > th {
        color: #64748b;
        font-weight: 600;
      }

      /* Group title reads as an editable heading, not a form field */
      .mrb-panel ::ng-deep .group-title.p-inputtext {
        background: transparent;
        border-color: transparent;
        box-shadow: none;
        font-weight: 600;
      }
      .mrb-panel ::ng-deep .group-title.p-inputtext:hover,
      .mrb-panel ::ng-deep .group-title.p-inputtext:focus {
        background: #fff;
        border-color: #cbd5e1;
      }
    `,
  ],
})
export class MultiRadioButtonCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(
    WidgetService<MultiRadioButtonCustomization>,
  );
  protected uniqueIdService = inject(UniqueIdService);

  customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    cap: this.fb.control<number | null>(0),
    groups: this.fb.array<GroupFormGroup>([]),
    required: [false],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    autocompleteRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
  });

  optionTypes = [
    { label: 'string', value: 'string' },
    { label: 'number', value: 'number' },
  ];

  topologyStyle = 'flex-row gap-5';

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        cap: widget.customization.cap,
        required: widget.customization.required,
        showIfRule: widget.customization.showIfRule,
        autocompleteRule: widget.customization.autocompleteRule,
      });

      this.customizationForm.controls.groups.clear();
      (widget.customization.groups ?? []).forEach((group: MultiRadioGroup) => {
        this.addGroup(group);
      });
    });
  }

  /** Build a stable, unique-within-widget key for a new group. */
  private nextGroupKey(): string {
    const existing = this.customizationForm.controls.groups.controls
      .map((g) => g.controls.key.value ?? '')
      .map((k) => Number.parseInt(k.replace('group_', ''), 10))
      .filter((n) => !Number.isNaN(n));
    const next = existing.length ? Math.max(...existing) + 1 : 1;
    return `group_${next}`;
  }

  addGroup(config: Partial<MultiRadioGroup> = {}) {
    const groupNumber = this.customizationForm.controls.groups.length + 1;
    const group: GroupFormGroup = this.fb.group({
      key: this.fb.control<string | null>(config.key ?? this.nextGroupKey()),
      label: this.fb.control<string | null>(
        config.label ?? `Gruppo ${groupNumber}`,
      ),
      defaultValue: this.fb.control<string | number | null>(
        config.defaultValue ?? '',
      ),
      options: this.fb.array<OptionFormGroup>([]),
    });

    this.customizationForm.controls.groups.push(group);

    (config.options ?? []).forEach((option) =>
      this.pushOption(group.controls.options, option),
    );
  }

  removeGroup(index: number) {
    this.customizationForm.controls.groups.removeAt(index);
  }

  addOption(groupIndex: number, config: Partial<SelectOption> = {}) {
    const group = this.customizationForm.controls.groups.at(groupIndex);
    if (!group) return;
    this.pushOption(group.controls.options, config);
  }

  removeOption(groupIndex: number, optionIndex: number) {
    const group = this.customizationForm.controls.groups.at(groupIndex);
    if (!group) return;
    group.controls.options.removeAt(optionIndex);
  }

  private pushOption(
    options: FormArray<OptionFormGroup>,
    config: Partial<SelectOption> = {},
  ) {
    const position = options.length + 1;
    const newOption: OptionFormGroup = this.fb.group({
      label: this.fb.control<string | null>(
        config.label ?? `Opzione ${position}`,
      ),
      value: this.fb.control<string | number | null>(
        config.value ?? `Opzione ${position}`,
      ),
      valueEqualsDescription: this.fb.control<boolean | null>(
        config.valueEqualsDescription ?? false,
      ),
      type: this.fb.control<'string' | 'number' | null>(
        config.type ?? 'string',
      ),
      weight: this.fb.control<number | null>(config.weight ?? 0),
    });

    // Sync value with label if valueEqualsDescription is true
    newOption.controls.label.valueChanges.subscribe((val) => {
      if (newOption.controls.valueEqualsDescription.value) {
        newOption.controls.value.setValue(val);
      }
    });

    // Adjust value type based on selected type
    newOption.controls.type.valueChanges.subscribe((val) => {
      if (val === 'string') {
        newOption.controls.value.setValue(
          newOption.controls.value.value?.toString() ?? '',
        );
      } else {
        const tmp = parseFloat(
          newOption.controls.value.value?.toString() ?? '',
        );
        if (!isNaN(tmp)) {
          newOption.controls.value.setValue(tmp);
        } else {
          newOption.controls.type.setValue('string', { emitEvent: false });
        }
      }
    });

    // Adjust type if value input changes
    newOption.controls.value.valueChanges.subscribe((val) => {
      if (!val) return;

      const tmp = parseFloat(val.toString() ?? '');
      if (isNaN(tmp)) {
        newOption.controls.type.setValue('string', { emitEvent: false });
      }
      if (val != newOption.controls.label.value) {
        newOption.controls.valueEqualsDescription.setValue(false, {
          emitEvent: false,
        });
      }
    });

    // Sync value with label when valueEqualsDescription is toggled
    newOption.controls.valueEqualsDescription.valueChanges.subscribe((val) => {
      if (val) {
        newOption.controls.value.setValue(newOption.controls.label.value, {
          emitEvent: false,
        });
      }
    });

    options.push(newOption);
  }

  updateWidget(closed: boolean) {
    if (!closed) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;

    const groups: MultiRadioGroup[] =
      this.customizationForm.controls.groups.controls.map((groupCtrl) => ({
        key: groupCtrl.controls.key.value ?? '',
        label: groupCtrl.controls.label.value ?? '',
        defaultValue: groupCtrl.controls.defaultValue.value ?? '',
        options: groupCtrl.controls.options.controls.map((optionCtrl) => ({
          label: optionCtrl.controls.label.value ?? '',
          value: optionCtrl.controls.value.value ?? '',
          valueEqualsDescription:
            optionCtrl.controls.valueEqualsDescription.value ?? false,
          type: optionCtrl.controls.type.value ?? 'string',
          weight: optionCtrl.controls.weight.value ?? 0,
        })),
      }));

    this.widgetService.updateWidget(id, {
      description: formValue.description ?? '',
      cap: formValue.cap ?? 0,
      groups,
      required: formValue.required ?? false,
      showIfRule: formValue.showIfRule || '',
      autocompleteRule: formValue.autocompleteRule || '',
    });
    this.widgetService.settingVisibility.set(false);
  }
}
